import FieldsInterface from "@app/Interface/FieldsInterface";
import TypeAssertions from "@app/utils/TypeAssertions";

export default class RegistrationFormFunctions {

    static getRegistrationFields = (): FieldsInterface => {

        const fields: FieldsInterface = {
            password: {
                fieldId: "password",
                formFieldId: "password",
                value: "",
                label: "Password",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter a password."
            },
            confirmPassword: {
                fieldId: "confirmPassword",
                formFieldId: "confirmPassword",
                value: "",
                label: "Confirm Password",
                validated: "default",
                required: true,
                helperTextInvalid: "Doesn't match password."
            },
            enableTwoFactor: {
                fieldId: "enableTwoFactor",
                formFieldId: "account-form-enableTwoFactor",
                value: true,
                label: "Enable 2FA",
                validated: "default",
                required: true,
                helperTextInvalid: ""
            },
            twoFactorType: {
                fieldId: "twoFactorType",
                formFieldId: "account-form-twoFactorType",
                value: "email",
                label: "Verification Type",
                validated: "default",
                required: false
            },
            twoFactorEmailAddress: {
                fieldId: "twoFactorEmailAddress",
                formFieldId: "account-form-email",
                value: "",
                label: "Email",
                validated: "default",
                required: true,
                validateAgainst: "isEmail",
                helperTextInvalid: "Please enter a valid email address."
            },
            twoFactorPhoneNumber: {
                fieldId: "twoFactorPhoneNumber",
                formFieldId: "account-form-twoFactorPhoneNumber",
                value: "",
                label: "Phone Number",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter a valid phone number."
            },
            firstName: {
                fieldId: "firstName",
                formFieldId: "firstName",
                value: "",
                label: "First Name",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter your first name."
            },
            lastName: {
                fieldId: "lastName",
                formFieldId: "lastName",
                value: "",
                label: "Last Name",
                validated: "default",
                required: true,
                helperTextInvalid: "Please enter your last name."
            },
            address1: {
                fieldId: "address1",
                formFieldId: "address1",
                value: "",
                label: "Address1",
                validated: "default",
                required: false,
                helperTextInvalid: ""
            },
            address2: {
                fieldId: "address2",
                formFieldId: "address2",
                value: "",
                label: "Address 2",
                validated: "default",
                required: false,
                helperTextInvalid: ""
            },
            city: {
                fieldId: "city",
                formFieldId: "city",
                value: "",
                label: "City",
                validated: "default",
                required: false,
                helperTextInvalid: ""
            },
            state: {
                fieldId: "state",
                formFieldId: "state",
                value: "",
                label: "State",
                validated: "default",
                required: false,
                helperTextInvalid: ""
            },
            zip: {
                fieldId: "zip",
                formFieldId: "zip",
                value: "",
                label: "Zip",
                validated: "default",
                required: false,
                helperTextInvalid: ""
            }

        };

        return fields;

    }

    static formValidation = (formFields: FieldsInterface): [boolean, FieldsInterface] => {

        let valid = true;

        const updatedFields = { ...formFields };

        const twoFactorEnabled = updatedFields["enableTwoFactor"].value === true ?? false;

        const noticeType = String(formFields["twoFactorType"].value);

        const newPassword = String(updatedFields["password"].value);
        const passwordStatus = TypeAssertions.isSecurePassword(newPassword);
        if (TypeAssertions.isStringNullOrEmpty(newPassword) || passwordStatus !== "good") {

            valid = false;

            updatedFields["password"].validated = "error";

            switch (passwordStatus) {
                case "length":
                    updatedFields["password"].helperTextInvalid = "Password should be have 6 or more characters.";
                    break;

                case "number":
                    updatedFields["password"].helperTextInvalid = "The password should have at least one number.";
                    break;

                case "special":
                    updatedFields["password"].helperTextInvalid = "The password should have at least one special character.";
                    break;

                case "uppercase":
                    updatedFields["password"].helperTextInvalid = "The password should have at least one capital letter.";
                    break;

                case "weak":
                    updatedFields["password"].helperTextInvalid = "The password should be stronger.";
                    break;

                default:
                    updatedFields["password"].helperTextInvalid = "Please enter a password.";
                    break;
            }

        } else {
            updatedFields["password"].validated = "default";
        }

        const confirmPassword = String(updatedFields["confirmPassword"].value);
        if (TypeAssertions.isStringNullOrEmpty(confirmPassword)) {
            valid = false;
            updatedFields["confirmPassword"].validated = "error";
        } else if (newPassword !== confirmPassword) {
            valid = false;
            updatedFields["confirmPassword"].validated = "error";
        } else {
            updatedFields["confirmPassword"].validated = "default";
        }

        // twoFactorEmailAddress
        if (twoFactorEnabled && noticeType === "email" && typeof formFields["twoFactorEmailAddress"].value === "string" && (TypeAssertions.isStringNullOrEmpty(formFields["twoFactorEmailAddress"].value) || TypeAssertions.isEmail(formFields["twoFactorEmailAddress"].value) === false)) {

            updatedFields["twoFactorEmailAddress"].validated = "error";

            valid = false;

        } else {

            updatedFields["twoFactorEmailAddress"].validated = "default";
        }

        // twoFactorPhoneNumber
        if (twoFactorEnabled && noticeType === "sms" && typeof formFields["twoFactorPhoneNumber"].value === "string" && (TypeAssertions.isStringNullOrEmpty(formFields["twoFactorPhoneNumber"].value) || TypeAssertions.isValidE164Number(formFields["twoFactorPhoneNumber"].value) === false)) {

            updatedFields["twoFactorPhoneNumber"].validated = "error";

            valid = false;

        } else {

            updatedFields["twoFactorPhoneNumber"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updatedFields["firstName"].value))) {
            valid = false;
            updatedFields["firstName"].validated = "error";
        } else {
            updatedFields["firstName"].validated = "default";
        }

        if (TypeAssertions.isStringNullOrEmpty(String(updatedFields["lastName"].value))) {
            valid = false;
            updatedFields["lastName"].validated = "error";
        } else {
            updatedFields["lastName"].validated = "default";
        }

        return [valid, updatedFields];

    }

}